import * as React from 'react';
import { SchoolEvent, SchoolEventTeamDataPlayer } from 'Src/models/event';
import {
  isEventStatusFinished,
  isMultipartyEvent,
  isShowIndividualScoreForTeam
} from '../../../../../../../helpers/event/event';
import {
  convertPointsToStringWithoutDelimeter,
  getIndividualScoreByStudent
} from '../../../../../../../helpers/event/schoolEventViewHelper';
import { convertPointsCricket, isCricket } from '../../../../../../../helpers/sport/cricket';
import { ChildrenEventTeamPlayerSubOrCaptain } from './TeamPlayerSubOrCaptain';
import { ChildrenEventTeamPlayerPosition } from './TeamPlayerPosition';
import { Child } from 'Src/models/child';
import { EventParticipantIdentity } from 'Src/components/EventParticipantIdentity/EventParticipantIdentity';
import { Report } from 'Src/models/report';
import { AvailabilityStatusDot } from 'Src/components/AvailabilityStatusDot/AvailabilityStatusDot';

interface Props {
  players: SchoolEventTeamDataPlayer[];
  event: SchoolEvent;
  ownedChildren: Child[];
  availabilityReports: Report[];
}

export function ChildrenEventTeamPlayers(props: Props) {
  const { players, event, ownedChildren, availabilityReports } = props;
  const ownedIds = new Set(ownedChildren.map(child => String(child.id)));
  const availabilityByPermission = availabilityReports.reduce((result, report) => {
    const key = String(report.playerDetails && report.playerDetails.permissionId || '');
    const previous = result.get(key);
    if (key && (!previous || new Date(report.createdAt).getTime() > new Date(previous.createdAt).getTime())) result.set(key, report);
    return result;
  }, new Map<string, Report>());
  const sortedPlayers = [...players].sort((a, b) => {
    const ownedDifference = Number(ownedIds.has(String(b.userId))) - Number(ownedIds.has(String(a.userId)));
    if (ownedDifference !== 0) return ownedDifference;
    return `${a.lastName || ''} ${a.firstName || ''}`.localeCompare(`${b.lastName || ''} ${b.firstName || ''}`);
  });

  const isShowIndividualScore = isShowIndividualScoreForTeam(event, players);

  const rows = sortedPlayers.map((player, playerIndex) => {
    const { userId, permissionId } = player;
    const isEventFinished = isEventStatusFinished(event);
    const playerPoints = getIndividualScoreByStudent(event, userId, permissionId);

    let playerPointsStr;

    if (isCricket(event) && !isMultipartyEvent(event)) {
      const { runs, wickets } = convertPointsCricket(playerPoints);
      playerPointsStr = `${runs} / ${wickets}`;
    } else {
      playerPointsStr = convertPointsToStringWithoutDelimeter(event, playerPoints);
    }

    const isShowPoints = (isEventFinished && isShowIndividualScore) || Boolean(playerPoints);
    const ownedChild = ownedChildren.find(child => String(child.id) === String(userId));
    const availability = availabilityByPermission.get(String(permissionId));

    return (
      <tr key={`school_players_${playerIndex}`} className={ownedChild ? 'eventParticipantRow--owned' : ''}>
        <td className="eventParticipantRow__number">
          <span>{playerIndex + 1}</span>
        </td>

        <td style={{ width: '28%' }}>
          <div className="parentAvailabilityIdentity">
            <EventParticipantIdentity
              firstName={player.firstName}
              lastName={player.lastName}
              isOwned={!!ownedChild}
              avatar={ownedChild && ownedChild.avatar}
              ownedLabel="Your child"
            />
            {ownedChild && <AvailabilityStatusDot report={availability} />}
          </div>
        </td>

        <td style={{ width: '15%' }}>
          <span className="ml-2 eventParticipantLastName">{player.lastName}</span>
        </td>

        <td style={{ width: '15%' }}>
          {isShowPoints && <span className={'ml-3 font-weight-bold'}>{`${playerPointsStr} `}</span>}
        </td>

        <td style={{ width: '24%' }}>
          <span className={'ePlayersPosition'}>
            <ChildrenEventTeamPlayerPosition player={player} event={event} />
          </span>
        </td>

        <td style={{ width: '13%' }}>
          <span className={'ePlayersSubOrCaptain'}>
            <ChildrenEventTeamPlayerSubOrCaptain player={player} event={event} />
          </span>
        </td>
      </tr>
    );
  });
  return (
    <table className={'w-100 eventParticipantRoster'}>
      <tbody>{rows}</tbody>
    </table>
  );
}
